import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import WebSocketService from '../services/WebSocketService'

const WebSocketContext = createContext()

// Audio for new transactions
const pingSound = new Audio(require('url:../../ping.mp3'))

export const WebSocketProvider = ({ children, apikey }) => {
  const [connectionState, setConnectionState] = useState('disconnected')
  const [transactions, setTransactions] = useState([])
  const [config, setConfig] = useState({ name: '', filters: [] })
  const [lastUpdate, setLastUpdate] = useState(null)
  const [error, setError] = useState(null)
  const [availableArticles, setAvailableArticles] = useState(null)
  const knownTransactionIds = useRef(new Set())
  const isInitialLoad = useRef(true)

  // Connect to WebSocket when apikey is available
  useEffect(() => {
    if (!apikey) {
      return
    }

    // Subscribe to WebSocket events
    const handleConnectionState = (state) => {
      setConnectionState(state)
      if (state === 'disconnected' || state === 'error') {
        setError(state === 'error' ? 'Erreur de connexion' : null)
      }
    }

    const handleConfig = (data) => {
      setConfig({
        name: data.name || '',
        filters: data.filters || []
      })
      setError(null)
    }

    const handleTransactions = (data) => {
      if (data.data) {
        // Sort by epoch ascending (oldest first)
        const sortedData = [...data.data].sort((a, b) => a.epoch - b.epoch)

        // Check for new transactions (not on initial load)
        if (!isInitialLoad.current) {
          const currentIds = new Set(sortedData.map(t => t.id))
          const hasNewTransaction = sortedData.some(t => !knownTransactionIds.current.has(t.id))

          if (hasNewTransaction) {
            pingSound.play().catch(e => console.log('Audio play failed:', e))
          }
        } else {
          isInitialLoad.current = false
        }

        // Update known transaction IDs
        knownTransactionIds.current = new Set(sortedData.map(t => t.id))

        setTransactions(sortedData)
      }
      if (data.datetime_update) {
        setLastUpdate(data.datetime_update)
      }
    }

    const handleError = (data) => {
      if (data.code === 401 || data.code === 'unauthorized') {
        setError('Tablette non autorisée')
      } else {
        setError(data.message || 'Erreur serveur')
      }
    }

    const handleArticles = (data) => {
      setAvailableArticles(data)
    }

    WebSocketService.subscribe('connectionState', handleConnectionState)
    WebSocketService.subscribe('config', handleConfig)
    WebSocketService.subscribe('transactions', handleTransactions)
    WebSocketService.subscribe('error', handleError)
    WebSocketService.subscribe('articles', handleArticles)

    // Connect
    WebSocketService.connect(apikey)

    // Cleanup on unmount
    return () => {
      WebSocketService.unsubscribe('connectionState', handleConnectionState)
      WebSocketService.unsubscribe('config', handleConfig)
      WebSocketService.unsubscribe('transactions', handleTransactions)
      WebSocketService.unsubscribe('error', handleError)
      WebSocketService.unsubscribe('articles', handleArticles)
      WebSocketService.disconnect()
    }
  }, [apikey])

  // Action: validate transaction
  const validate = useCallback((id, id_lieu) => {
    WebSocketService.send('validation', [{ id, id_lieu }])
  }, [])

  // Action: toggle ownership
  const toggleOwnership = useCallback((id, id_lieu) => {
    WebSocketService.send('toggleOwnership', [{ id, id_lieu }])
  }, [])

  // Action: add images
  const addImages = useCallback((id, id_lieu, images_uuid) => {
    WebSocketService.send('addImages', [{ id, id_lieu, images: images_uuid }])
  }, [])

  // Action: request available articles
  const requestArticles = useCallback((id, id_lieu) => {
    setAvailableArticles(null)
    WebSocketService.send('getArticles', [{ id, id_lieu }])
  }, [])

  // Action: change article
  const changeArticle = useCallback((id, id_lieu, article_id) => {
    WebSocketService.send('changeArticle', [{ id, id_lieu, article_id }])
  }, [])

  // Clear available articles
  const clearArticles = useCallback(() => {
    setAvailableArticles(null)
  }, [])

  // Manual reconnect
  const reconnect = useCallback(() => {
    if (apikey) {
      WebSocketService.disconnect()
      WebSocketService.connect(apikey)
    }
  }, [apikey])

  const value = {
    connectionState,
    transactions,
    config,
    lastUpdate,
    error,
    availableArticles,
    validate,
    toggleOwnership,
    addImages,
    requestArticles,
    changeArticle,
    clearArticles,
    reconnect
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocket = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}

export default WebSocketContext
